import { useMediaQuery } from '@mui/material';
import { getWeek } from 'date-fns';
import { FC, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { SecondaryTextTypography } from '../../../App.styles';
import { Store, StoreAddressItem } from '../../../global-state/types';
import { useAsyncAction, useStoreOpeningHours, useStores } from '../../../hooks';
import { Icon, Icons, Typography } from '../../../stories/atoms';
import { ColumnFlexContainer, RowFlexContainer } from '../../../stories/atoms/containerV2/ContainerV2';
import { Button, Modal } from '../../../stories/molecules';
import Copy from '../../../stories/molecules/copy/Copy';
import { muiTheme } from '../../../theme';
import { commonTranslations, homeTranslations } from '../../../translations';
import { areaResultTranslations } from '../../../translations/areaResult';
import { isFailureResponse } from '../../../utils';
import { OpeningHours } from '../../home/common/openingHours/OpeningHours';
import { isStoreVisibilityType, StoreVisibility, StoreVisibilityType } from '../storeVisibility/StoreVisibility';
import { StyledAnchor, StyledDivider } from './StoreDetailsModal.styles';

type StoreDetailsModalProps = {
  isOpen: boolean;
  storeId: string;
  onClose: () => void;
};

export const StoreDetailsModal: FC<StoreDetailsModalProps> = ({ isOpen, storeId, onClose }) => {
  const { t } = useTranslation();
  const isTablet = useMediaQuery(muiTheme.breakpoints.down('tabletLandscape'));
  const isSmallDesktop = useMediaQuery(muiTheme.breakpoints.down('desktop'));
  const { fetchStoresData } = useStores();
  const { getWeeklyOpeningHours } = useStoreOpeningHours();
  const { storeDetailsModal: storeDetailsModalTranslations } = areaResultTranslations;

  const {
    result: storeData,
    isLoading: storeDataLoading,
    isError: storeDataError,
    triggerAction: triggerFetchStoreData,
  } = useAsyncAction<Store>();

  const fetchStore = async () => {
    const response = await fetchStoresData(storeId);

    if (isFailureResponse(response)) {
      throw new Error();
    }
    return response.data;
  };

  const fetchData = async () => {
    triggerFetchStoreData(fetchStore);
  };

  useEffect(() => {
    fetchData();
  }, [storeId]);

  const getAddress = (storeAddresses: StoreAddressItem[]) => {
    const location = storeAddresses.find((item) => item.addressType === 'Location');

    if (location) {
      return `${location.streetAddress1}, ${location.postalCity}, ${location.zipCode}`;
    }
  };

  return (
    <Modal isOpen={isOpen} handleClose={onClose}>
      <ColumnFlexContainer
        data-testid="store-details-modal-container"
        padding={isTablet ? [6] : [8]}
        width={isTablet ? '100%' : '500px'}
        gap={isSmallDesktop ? [2] : [0]}
        height={isTablet ? '100%' : [140]}
      >
        <Typography type="h4">
          {t(
            storeDetailsModalTranslations.storeDetailsText.key,
            storeDetailsModalTranslations.storeDetailsText.defaultValue,
          )}
        </Typography>
        <RowFlexContainer gap={[2]} alignItems="center" justifyContent="start">
          <Icon icon={Icons.STORE} size="small" />
          <Typography type="h4">{storeId}</Typography>
          {isStoreVisibilityType(storeData?.status) && (
            <StoreVisibility type={storeData?.status as StoreVisibilityType} />
          )}
        </RowFlexContainer>
        {storeDataLoading && (
          <ColumnFlexContainer
            key="loading-store-details"
            justifyContent="center"
            alignItems="center"
            gap={[2]}
            height="100%"
            data-testid="loading-store-view"
          >
            <Icon icon={Icons.SPINNER} size="small" />
            <Typography type="body2">
              {t(
                storeDetailsModalTranslations.loadingText.key,
                storeDetailsModalTranslations.loadingText.defaultValue,
              )}
              ...
            </Typography>
          </ColumnFlexContainer>
        )}
        {storeDataError && !storeDataLoading && (
          <ColumnFlexContainer
            key="error-store-details"
            justifyContent="center"
            alignItems="center"
            gap={[2]}
            height="100%"
            data-testid="error-store-view"
          >
            <Typography type="h4">
              {t(commonTranslations.errorText1.key, commonTranslations.errorText1.defaultValue)}
            </Typography>
            <Typography type="body2">
              {t(commonTranslations.errorText2.key, commonTranslations.errorText2.defaultValue)}
            </Typography>
            <Button
              label={t(commonTranslations.retry.key, commonTranslations.retry.defaultValue)}
              onClick={fetchData}
              buttonContentProps={{ iconOptions: { customIcon: <Icon icon={Icons.REFRESH} /> } }}
            />
          </ColumnFlexContainer>
        )}

        {!storeDataError && !storeDataLoading && storeData && (
          <>
            <StyledDivider $isTablet={isTablet} />
            <ColumnFlexContainer
              height={isTablet ? '100%' : [100]}
              overflow="auto"
              gap={isSmallDesktop ? [2] : [0]}
            >
              <ColumnFlexContainer gap={[5]}>
                <ColumnFlexContainer gap={[2]}>
                  <SecondaryTextTypography type="caption">
                    {t(
                      storeDetailsModalTranslations.address.key,
                      storeDetailsModalTranslations.address.defaultValue,
                    )}
                  </SecondaryTextTypography>
                  {storeData.popularName && (
                    <RowFlexContainer gap={[2]}>
                      <Icon icon={Icons.BAG} size="small" />
                      <Typography>{storeData.popularName}</Typography>
                    </RowFlexContainer>
                  )}
                  {storeData.addresses && (
                    <RowFlexContainer gap={[2]}>
                      <Icon icon={Icons.LOCATION} size="small" />
                      <Typography>{getAddress(storeData.addresses.item)}</Typography>
                    </RowFlexContainer>
                  )}
                </ColumnFlexContainer>

                <ColumnFlexContainer gap={[2]}>
                  <SecondaryTextTypography type="caption">
                    {t(
                      storeDetailsModalTranslations.storeManager.key,
                      storeDetailsModalTranslations.storeManager.defaultValue,
                    )}
                  </SecondaryTextTypography>
                  {!storeData.storeManager && (
                    <Typography type="body3">
                      {t(
                        storeDetailsModalTranslations.noManagerText.key,
                        storeDetailsModalTranslations.noManagerText.defaultValue,
                      )}
                    </Typography>
                  )}
                  {storeData.storeManager && (
                    <>
                      <RowFlexContainer gap={[2]}>
                        <Icon icon={Icons.PROFILE} size="small" />
                        <Typography>
                          {storeData.storeManager.firstName} {storeData.storeManager.lastName}
                        </Typography>
                      </RowFlexContainer>
                      <RowFlexContainer>
                        <RowFlexContainer gap={[2]} flex={1}>
                          <Icon icon={Icons.EMAIL} size="small" />
                          <Typography>
                            <StyledAnchor href={`mailto:${storeData.storeManager.upn}`}>
                              {storeData.storeManager.upn}
                            </StyledAnchor>
                          </Typography>
                        </RowFlexContainer>
                        <Copy textToCopy={storeData.storeManager.upn} />
                      </RowFlexContainer>
                    </>
                  )}
                </ColumnFlexContainer>

                <ColumnFlexContainer gap={[2]}>
                  <SecondaryTextTypography type="caption">
                    {t(
                      storeDetailsModalTranslations.services.key,
                      storeDetailsModalTranslations.services.defaultValue,
                    )}
                  </SecondaryTextTypography>
                  <Typography type="body3">
                    {t(
                      storeDetailsModalTranslations.noServicesFound.key,
                      storeDetailsModalTranslations.noServicesFound.defaultValue,
                    )}
                  </Typography>
                </ColumnFlexContainer>

                <ColumnFlexContainer gap={[2]}>
                  <SecondaryTextTypography type="caption">
                    {t(
                      homeTranslations.slider.storeOpeningHours.key,
                      homeTranslations.slider.storeOpeningHours.defaultValue,
                    )}
                    , W {getWeek(new Date())}
                  </SecondaryTextTypography>
                  {!storeData.openingHours && (
                    <Typography type="body3">
                      {t(
                        storeDetailsModalTranslations.noOpeningHourText.key,
                        storeDetailsModalTranslations.noOpeningHourText.defaultValue,
                      )}
                    </Typography>
                  )}

                  {storeData.openingHours && <OpeningHours openingHours={getWeeklyOpeningHours(storeData)} />}
                </ColumnFlexContainer>
              </ColumnFlexContainer>
            </ColumnFlexContainer>
          </>
        )}
      </ColumnFlexContainer>
    </Modal>
  );
};
